import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  from,
  InMemoryCache,
  NormalizedCacheObject,
  Reference,
} from "@apollo/client";
import { InvalidationPolicyCache } from "apollo-invalidation-policies";
import { RetryLink } from "apollo-link-retry";

import { Auth } from "aws-amplify";
import { createAuthLink, AUTH_TYPE, AuthOptions } from "aws-appsync-auth-link";
import { createSubscriptionHandshakeLink } from "aws-appsync-subscription-link";

global.fetch = require("isomorphic-fetch");

const activeEnv = process.env.GATSBY_ENV || process.env.NODE_ENV || "development";

const dotEnvResult = require("dotenv").config({
  path: `.env.${activeEnv}`,
});

if (dotEnvResult.parsed) {
  Object.assign(process.env, dotEnvResult.parsed);
}

interface AuthLink {
  url: string;
  region: string;
  auth: AuthOptions;
  disableOffline?: boolean;
}

const awsConfig = {
  graphQlEndpoint: process.env.GATSBY_APP_SYNC_GRAPHQL_ENDPOINT,
  region: process.env.GATSBY_REGION,
};

const apiKeyAuthConfig: AuthLink = {
  url: awsConfig.graphQlEndpoint,
  region: awsConfig.region,
  disableOffline: true,
  auth: {
    type: AUTH_TYPE.API_KEY,
    apiKey: process.env.GATSBY_APP_SYNC_API_KEY,
  },
};

const userPoolAuthConfig: AuthLink = {
  url: awsConfig.graphQlEndpoint,
  region: awsConfig.region,
  disableOffline: true,
  auth: {
    type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
    jwtToken: async (): Promise<string | undefined> => {
      try {
        return (await Auth.currentSession()).getIdToken().getJwtToken();
      } catch (_err) {
        return;
      }
    },
  },
};

const cache = new InvalidationPolicyCache({
  typePolicies: {
    User: {
      keyFields: [],
    },
    Wallet: {
      keyFields: [],
    },
    WalletExternalRefs: {
      keyFields: false,
    },
    Query: {
      fields: {
        getLinkedCards: {
          merge(_existing: Reference[] | undefined, incoming: Reference[]): Reference[] {
            if (incoming === []) {
              return [];
            } else {
              return incoming;
            }
          },
        },
      },
    },
  },
  invalidationPolicies: {
    timeToLive: 3600,
  },
});

const createLink = (config: AuthLink): ApolloLink => {
  const httpLink = createHttpLink({ uri: config.url });

  return from([
    // Retries network connections automatically if they fail
    new RetryLink(),
    // AppSync connection links
    createAuthLink({
      url: config.url,
      region: config.region,
      auth: config.auth,
    }) as any,
    createSubscriptionHandshakeLink(config.url as any, httpLink as any),
  ]);
};

const ApiKeyClient: ApolloClient<NormalizedCacheObject> = new ApolloClient({
  link: createLink(apiKeyAuthConfig),
  cache: new InMemoryCache(),
  connectToDevTools: activeEnv === "development" ? true : false,
});

const UserPoolClient: ApolloClient<NormalizedCacheObject> = new ApolloClient({
  link: createLink(userPoolAuthConfig),
  cache,
  connectToDevTools: activeEnv === "development" ? true : false,
});

export { ApiKeyClient, UserPoolClient };
